import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search, Check } from "lucide-react";

export default function Combobox({ value, onChange, options, placeholder = "Sélectionner…", disabled = false, testid, emptyText = "Aucun résultat", allowCustom = true, optionIcon }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter((o) => o.toLowerCase().includes(query.trim().toLowerCase()));
  const exact = options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

  const pick = (val) => { onChange(val); setOpen(false); setQuery(""); };

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} data-testid={testid ? `${testid}-trigger` : undefined}
        className={`w-full flex items-center justify-between gap-2 bg-[#050810] border border-[#1A2642] text-left rounded-md px-4 py-3 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent ${disabled ? "opacity-40 cursor-not-allowed" : "hover:border-[#00B4D8]/60"}`}>
        <span className={`flex items-center gap-2 min-w-0 ${value ? "text-white" : "text-[#475569]"}`}>
          {optionIcon && value && (
            <img src={optionIcon(value)} alt="" onError={(e) => { e.target.style.display = "none"; }}
              className="w-14 h-14 object-contain shrink-0" />
          )}
          <span className="truncate">{value || placeholder}</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-[#94A3B8] shrink-0 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#0C1322] border border-[#1A2642] rounded-md shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1A2642]">
            <Search className="w-4 h-4 text-[#94A3B8]" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher…"
              data-testid={testid ? `${testid}-search` : undefined}
              className="w-full bg-transparent text-sm text-white placeholder:text-[#475569] focus:outline-none" />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && !allowCustom && (
              <p className="px-4 py-3 text-sm text-[#94A3B8]">{emptyText}</p>
            )}
            {filtered.map((o) => (
              <button type="button" key={o} onClick={() => pick(o)} data-testid={testid ? `${testid}-option-${o}` : undefined}
                className="w-full flex items-center justify-between gap-2 text-left px-4 py-2 text-sm text-white hover:bg-[#00B4D8]/10 transition">
                <span className="flex items-center gap-2 min-w-0">
                  {optionIcon && (
                    <img src={optionIcon(o)} alt="" onError={(e) => { e.target.style.visibility = "hidden"; }}
                      className="w-14 h-14 object-contain shrink-0" />
                  )}
                  <span className="truncate">{o}</span>
                </span>
                {value === o && <Check className="w-4 h-4 text-[#00B4D8] shrink-0" />}
              </button>
            ))}
            {allowCustom && query.trim() && !exact && (
              <button type="button" onClick={() => pick(query.trim())} data-testid={testid ? `${testid}-custom` : undefined}
                className="w-full text-left px-4 py-2 text-sm text-[#FFD60A] hover:bg-[#FFD60A]/10 transition border-t border-[#1A2642]">
                Utiliser « {query.trim()} »
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
